import React, { useEffect, useState } from "react";
import { useTable, useSortBy, useFilters, useGlobalFilter } from "react-table";
import {
  ChakraProvider, Table, Thead, Tbody, Tr, Th, Td, chakra, Flex, Spacer, Heading, Text, Box,
  Tabs, TabPanels, TabPanel
} from "@chakra-ui/react";
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons'
import TableHistCLN from './TableHistCLN'


// This is a custom filter UI for selecting
// a unique option from a list
function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id }
}) {
  // Calculate the options for filtering
  // using the preFilteredRows
  const options = React.useMemo(() => {
    const options = new Set();
    preFilteredRows.forEach((row) => {
      if (id === 'name') {
        options.add(row.values[id].substring(0, row.values[id].indexOf(' ')));
      } else {
        options.add(row.values[id]);
      }
    });
    return [...options.values()];
  }, [id, preFilteredRows]);

  // Render a multi-select box
  return (
    <select
      style={{ width: "60px", backgroundColor: "#EDF2F7" }}
      value={filterValue}
      onChange={(e) => {
        setFilter(e.target.value || undefined);
      }}
    >
      <option value="">Все</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}


function CustomTable({ columns, data }) {

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: 'dohod_pos_mzl',
            desc: true
          }
        ],
      }
    },
    useFilters, // useFilters!
    useGlobalFilter, // useGlobalFilter!
    useSortBy,
    (hooks) => {
      hooks.allColumns.push((columns) => [
        ...columns,
      ]);
    }
  );

  return (
    <>
    <Box
      w="90%"
      overflowX="auto"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
      bg="white"
      boxShadow="0 18px 45px rgba(49, 46, 129, 0.12)"
    >
      <Table
        {...getTableProps()}
        w="100%"
        variant="simple"
        fontSize="sm"
        sx={{
          borderCollapse: "separate",
          borderSpacing: 0,

          "thead tr th:first-of-type": {
            borderTopLeftRadius: "5px",
          },

          "thead tr th:last-of-type": {
            borderTopRightRadius: "5px",
          },
        }}
      >
        <Thead
          bgGradient="linear(to-r, indigo.600, purple.600)"
        >
          {headerGroups.map((headerGroup) => {
            const { key, ...restHeaderGroupProps } =
              headerGroup.getHeaderGroupProps();

            return (
              <Tr key={key} {...restHeaderGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key, ...restColumn } = column.getHeaderProps([
                    column.getSortByToggleProps(),
                    { style: column.style },
                  ]);

                  return (
                                    <Th key={column.id}
                      {...restColumn}
                      isNumeric={column.isNumeric}
                      sx={{textAlign: 'center', p:'4px', fontSize:"x-small"}}
                    >
                      {column.render('Header')}
                      <chakra.span>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <TriangleDownIcon aria-label='sorted descending' />
                          ) : (
                            <TriangleUpIcon aria-label='sorted ascending' />
                          )
                        ) : column.canSort ? (
                            <Text>⬍</Text>
                        ) : null}
                      </chakra.span>
                      {/* Render the columns filter UI */}
                      <div sx={{textAlign: 'center'}}>
                        {column.canFilter ? column.render("Filter") : null}
                      </div>
                    </Th>
                  );
                })}
              </Tr>
            );
          })}
        </Thead>

        <Tbody>
          {rows.map((row) => {
            prepareRow(row);
            const { key, ...restRowProps } = row.getRowProps();

            return (
              <Tr
                key={key}
                {...restRowProps}
                transition="all 0.18s ease"
                _even={{ bg: "gray.50" }}
                _hover={{
                  bg: "indigo.50",
                  transform: "scale(1.002)",
                  boxShadow: "inset 4px 0 0 #5A67D8",
                }}
              >
                {row.cells.map((cell) => {
                  const { key, ...restCellProps } = cell.getCellProps();

                  return (
                    <Td
                      key={key}
                      {...restCellProps}
                      isNumeric={cell.column.isNumeric}
                      px={3}
                      py={2}
                      color="gray.700"
                      borderColor="gray.100"
                      fontWeight="500"
                      textAlign={cell.column.isNumeric ? "right" : "left"}
                    >
                      {cell.render("Cell")}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
    </>
  );
}

export default function App() {

  const columns = React.useMemo(
    () => [
      {
        Header: 'ИНФО',
        columns: [
          {
            Header: 'Ко',
            accessor: 'name',
            disableSortBy: true,
            Filter: SelectColumnFilter,
            Cell: ({ row }) => {
              if (row.original.stop_otgr === 1) {
                return <div style={{ backgroundColor: "red", whiteSpace: 'normal' }}>
                  {row.original.name}
                </div>;
              } else {
                return (
                  <div style={{ whiteSpace: 'normal' }}>{row.original.name}</div>
                )
              }
            }
          },
          {
            Header: 'ПЗУ',
            accessor: 'pzy',
            disableSortBy: true,
            Filter: SelectColumnFilter,
          },
          {
            Header: 'Дата изм.',
            accessor: 'data',
            disableFilters: true,
            Cell: row => <div style={{ textAlign: "center" }}>{row.value}</div>
          },
          {
            Header: 'Вид лома',
            accessor: 'vid_loma',
            disableSortBy: true,
            Filter: SelectColumnFilter,
            Cell: row => <div style={{ textAlign: "center" }}>{row.value}</div>
          },
        ],
        style: {backgroundColor: '#fab95b', color: 'black'},
      },
      {
        Header: 'VIP',
        columns: [
          {
            Header: 'Тр. Пост',
            accessor: 'vip_pos',
            sortInverted: true,
            disableFilters: true,
            Cell: ({ row }) => {
              return <div style={{ textAlign: "center" }}>
                {row.original.stop_otgr === 1 ? "СТОП" :
                (row.original.vip_pos < 0) ? 0 :
                row.original.vip_pos.toLocaleString('de-CH')}
              </div>;
            }
          },
          {
            Header: 'Тр. Покуп',
            accessor: 'vip_pok',
            sortInverted: true,
            disableFilters: true,
            Cell: ({ row }) => {
              return <div style={{ textAlign: "center" }}>
                {row.original.stop_otgr === 1 ? "СТОП" :
                (row.original.vip_pok < 0) ? 0 :
                row.original.vip_pok.toLocaleString('de-CH')}
              </div>;
            }
          },
          {
            Header: 'Тр. Рудмет',
            accessor: 'vip_rud',
            sortInverted: true,
            disableFilters: true,
            Cell: ({ row }) => {
              return <div style={{ textAlign: "center" }}>
                {row.original.stop_otgr === 1 ? "СТОП" :
                (row.original.vip_rud < 0) ? 0 :
                row.original.vip_rud.toLocaleString('de-CH')}
              </div>;
            }
          },
        ],
        style: {backgroundColor: '#1a3263', color: 'white'},
      },

    ],
    [],
  )

  const current = new Date();
  const date = `${current.getDate()}/${current.getMonth()+1}/${current.getFullYear()}`;

  const [data_m, setData_m] = useState([]); // use an empty array as initial value

  const checkUser = async () => {
    let username;
    let us_id = Number(localStorage.getItem('session'))
    if (localStorage.getItem('me3To_keN9rud')) {
      username = 'Администратор';
    } else if (localStorage.getItem('me3To_ke_uch')){
      username = 'Сотрудник';
    } else {
      username = 'Менеджер';
    }
    const response = await fetch(`/api/check_auth/${us_id}`, {method: "GET"})
    const data = await response.json()
    if (data.data.length === 0) {
      if (username === 'Администратор') {
        localStorage.removeItem('me3To_keN9rud')
        localStorage.removeItem('session')
      } else if (username === 'Сотрудник' || us_id === '1111657203') {
        localStorage.removeItem('me3To_ke_uch')
        localStorage.removeItem('session')
      } else {
        localStorage.removeItem('me3To_keN9')
        localStorage.removeItem('session')
      }
    }
  };

  const fetchPrice = async () => {
    const response = await fetch("/api/vip/hfieofw23")
    const data_m = await response.json()
    setData_m(data_m.data)
  }

  useEffect(() => {
    fetchPrice();
    checkUser();
  }, []);

  return (
    <ChakraProvider>
      <Heading p='2' as='h4' size='md'>
        Дата: {date}
      </Heading>
      <Tabs isFitted >
        <TabPanels overflowX='scroll'>
          <TabPanel>
            <CustomTable columns={columns} data={data_m} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </ChakraProvider>
  );
}